import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthContext } from './authContext';
import { apiFetch, clearToken, getToken, setToken, ApiError } from './apiClient';
import type { AuthContextValue, AuthStatus, LoginResult, User } from './authTypes';
import { browserTimeZone } from '../lib/datetime';

/** Device name sent on login so the SPA receives a bearer token. */
const DEVICE_NAME = 'deployhub-web';

/** Envelope shape of the login response. */
interface LoginResponse {
  data: User;
  token: string | null;
}

/** Envelope shape of the `/auth/me` response. */
interface MeResponse {
  data: User;
}

/** Props for {@link AuthProvider}. */
export interface AuthProviderProps {
  /** Application subtree that consumes the auth session. */
  children: ReactNode;
}

/**
 * Owns the authentication session (spec 08 §1 — no business logic client-side;
 * this is session state + transport only).
 *
 * On mount, if a token is persisted it calls `/auth/me` to restore the user; a
 * 401 (handled in {@link apiFetch}) clears the token and drops to unauthenticated.
 * `login` exchanges credentials for a bearer token (via `device_name`), and
 * `logout` always clears the local token even if the network call fails.
 */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(() =>
    getToken() !== null ? 'loading' : 'unauthenticated',
  );
  // Guards against a state update after unmount during the restore call.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Restore an existing session on first load.
  useEffect(() => {
    if (getToken() === null) {
      return;
    }
    let active = true;
    void (async () => {
      try {
        const response = await apiFetch<MeResponse>('/auth/me');
        if (active && mounted.current) {
          setUser(response.data);
          setStatus('authenticated');
        }
      } catch {
        clearToken();
        if (active && mounted.current) {
          setUser(null);
          setStatus('unauthenticated');
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string, mfaCode?: string): Promise<LoginResult> => {
      // `mfa_code` is only sent on the second leg; omitting it on the first leg is
      // what makes an MFA-enrolled account return the `mfa_required` challenge.
      const body: Record<string, string> = { email, password, device_name: DEVICE_NAME };
      if (mfaCode !== undefined && mfaCode !== '') {
        body.mfa_code = mfaCode;
      }
      // Browser-detected zone (spec 08 §9): the API uses it only to seed a brand-new
      // account's display timezone on first sign-in, never to override a choice.
      const detectedZone = browserTimeZone();
      if (detectedZone !== null) {
        body.timezone = detectedZone;
      }

      let response: LoginResponse;
      try {
        response = await apiFetch<LoginResponse>('/auth/login', { method: 'POST', body });
      } catch (error) {
        // A correct password on an MFA-enrolled account returns 401 with the
        // `mfa_required` extension member. That is not a failure — it is the
        // signal to prompt for a second factor, so translate it into a result
        // instead of rethrowing. Every other error (bad password, wrong code,
        // throttle, inactive account) propagates for the caller to render.
        if (error instanceof ApiError && error.status === 401 && error.problem?.mfa_required === true) {
          const method = error.problem.mfa_method;
          return typeof method === 'string'
            ? { status: 'mfa_required', method }
            : { status: 'mfa_required' };
        }
        throw error;
      }

      if (response.token === null) {
        // The SPA authenticates by bearer token; a null token without the
        // `mfa_required` signal is an unexpected contract violation, not a state
        // we can recover from — fail loudly rather than silently hang.
        throw new ApiError(200, { title: 'Additional verification required' }, null);
      }

      setToken(response.token);
      if (mounted.current) {
        setUser(response.data);
        setStatus('authenticated');
      }
      return { status: 'authenticated' };
    },
    [],
  );

  const sendEmailOtp = useCallback(async (email: string, password: string): Promise<void> => {
    // Public, throttled pre-auth step: re-posts the credentials so the backend can
    // mail a login-time code for an email-OTP account. No token is issued and the
    // response is intentionally generic (no account/credential signal), so we only
    // need to know the call completed. Transport/throttle errors propagate for the
    // caller to render (e.g. a 429). No session state changes here.
    await apiFetch<{ message?: string }>('/auth/mfa/email/send', {
      method: 'POST',
      body: { email, password },
    });
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    // Re-read the canonical user (e.g. after MFA was enabled/disabled). A 401 is
    // handled by apiFetch (token cleared) and rethrown for the caller to react.
    const response = await apiFetch<MeResponse>('/auth/me');
    if (mounted.current) {
      setUser(response.data);
    }
  }, []);

  const completeSso = useCallback(async (token: string): Promise<void> => {
    // The token was minted by the backend SSO callback and handed to the SPA in
    // the URL fragment. Persist it, then restore the user exactly like a normal
    // session so the rest of the app is identical to a password sign-in. An
    // invalid token makes `/auth/me` 401, which clears it and rethrows.
    setToken(token);
    try {
      const response = await apiFetch<MeResponse>('/auth/me');
      if (mounted.current) {
        setUser(response.data);
        setStatus('authenticated');
      }
    } catch (error) {
      clearToken();
      if (mounted.current) {
        setUser(null);
        setStatus('unauthenticated');
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiFetch<void>('/auth/logout', { method: 'POST' });
    } catch {
      /* Sign out locally regardless of the server response. */
    } finally {
      clearToken();
      if (mounted.current) {
        setUser(null);
        setStatus('unauthenticated');
      }
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, sendEmailOtp, refresh, completeSso, logout }),
    [status, user, login, sendEmailOtp, refresh, completeSso, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
